/**
 * Finance / caisse data layer — the ONLY place this feature talks to the server. Every path and
 * capability string below is code-verified against server/src/modules (finance_billing). Server
 * data lives only in TanStack Query; mutations invalidate the affected lab-scoped keys.
 *
 * Endpoints consumed (all under /api/v1):
 *   POST /caisse/sessions               finance.caisse.open
 *   GET  /caisse/sessions/courante      finance.caisse.read
 *   GET  /caisse/sessions/:id/stats     finance.caisse.read
 *   POST /caisse/sessions/:id/cloture   finance.caisse.close
 *   POST /factures                      finance.facture.issue
 *   POST /paiements                     finance.paiement.collect  (+ requireOpenCaisse gate)
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  AccrualResult,
  AddMouvementResult,
  BaremeFidelite,
  CancelMouvementResult,
  CloseZResult,
  CorrectionCaisse,
  CorrectionType,
  Facture,
  FactureLigne,
  Paiement,
  PaiementMode,
  PostCorrectionResult,
  Remise,
  ReopenResult,
  ReverseResult,
  Sens,
  SessionCaisse,
  SessionStats,
  WireId,
  XReport,
} from "./types";

/** The server capability keys this feature gates on (exact server keys). */
export const FINANCE_CAPS = {
  caisseRead: "finance.caisse.read",
  caisseOpen: "finance.caisse.open",
  caisseClose: "finance.caisse.close",
  factureIssue: "finance.facture.issue",
  paiementCollect: "finance.paiement.collect",
  // ── finance (06) cash-desk extensions ──────────────────────────────────────
  caisseReopen: "finance.caisse.reopen",
  mouvementCreate: "finance.mouvement.create",
  paiementRefund: "finance.paiement.refund",
  clotureRead: "finance.cloture.read",
  fideliteRead: "finance.fidelite.read",
  fideliteManage: "finance.fidelite.manage",
} as const;

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the finance feature. */
export const financeKeys = {
  sessionCourante: (labId: string | null) => qk.scoped(labId, "finance", "caisse", "courante"),
  sessionStats: (labId: string | null, sessionId: string) =>
    qk.scoped(labId, "finance", "caisse", "stats", sessionId),
  // ── finance (06) ───────────────────────────────────────────────────────────
  xReport: (labId: string | null, sessionId: string) =>
    qk.scoped(labId, "finance", "caisse", "x-report", sessionId),
  corrections: (labId: string | null, sessionId: string) =>
    qk.scoped(labId, "finance", "caisse", "corrections", sessionId),
  bareme: (labId: string | null) => qk.scoped(labId, "finance", "fidelite", "bareme"),
};

// ── Queries ────────────────────────────────────────────────────────────────────

/** The current open session, or null when no session is open. */
export function useCurrentSession(labId: string | null, enabled: boolean) {
  return useApiQuery<SessionCaisse | null>(
    financeKeys.sessionCourante(labId),
    "/caisse/sessions/courante",
    { enabled, staleTime: 10_000 },
  );
}

/** Running stats for an open session (count + per-mode totals). */
export function useSessionStats(labId: string | null, sessionId: string | null, enabled: boolean) {
  return useApiQuery<SessionStats>(
    financeKeys.sessionStats(labId, sessionId ?? "_"),
    `/caisse/sessions/${sessionId ?? "_"}/stats`,
    { enabled: enabled && !!sessionId, staleTime: 10_000 },
  );
}

// ── Mutations ────────────────────────────────────────────────────────────────────

export interface OpenSessionVars {
  caisse_id: string;
  fond_de_caisse: number;
}

/** POST /caisse/sessions — open a session; refreshes the "courante" query on success. */
export function useOpenSession(labId: string | null) {
  return useApiMutation<SessionCaisse, OpenSessionVars>(
    (vars) => api.post<SessionCaisse>("/caisse/sessions", vars),
    { invalidate: [financeKeys.sessionCourante(labId)] },
  );
}

export interface CloseSessionVars {
  sessionId: string;
  especes_comptees: number;
}

/** POST /caisse/sessions/:id/cloture — close + Z-report; invalidates courante + stats. */
export function useCloseSession(labId: string | null) {
  return useApiMutation<SessionCaisse, CloseSessionVars>(
    ({ sessionId, especes_comptees }) =>
      api.post<SessionCaisse>(`/caisse/sessions/${sessionId}/cloture`, { especes_comptees }),
    {
      invalidate: [financeKeys.sessionCourante(labId)],
    },
  );
}

export interface IssueFactureVars {
  demande_analyse_id?: string;
  lignes: FactureLigne[];
  remise?: Remise;
}

/** POST /factures — issue an invoice. No server list endpoint exists, so no invalidation. */
export function useIssueFacture() {
  return useApiMutation<Facture, IssueFactureVars>((vars) => {
    const body: Record<string, unknown> = { lignes: vars.lignes };
    if (vars.demande_analyse_id) body.demande_analyse_id = vars.demande_analyse_id;
    if (vars.remise) body.remise = vars.remise;
    return api.post<Facture>("/factures", body);
  });
}

export interface CollectPaiementVars {
  facture_id: string;
  montant: number;
  montant_tendu: number;
  mode?: PaiementMode;
}

/** POST /paiements — record a payment (server requires an open caisse). Refreshes session stats. */
export function useCollectPaiement(labId: string | null, sessionId: string | null) {
  return useApiMutation<Paiement, CollectPaiementVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        facture_id: vars.facture_id,
        montant: vars.montant,
        montant_tendu: vars.montant_tendu,
      };
      if (vars.mode) body.mode = vars.mode;
      return api.post<Paiement>("/paiements", body);
    },
    {
      invalidate: sessionId
        ? [financeKeys.sessionStats(labId, sessionId), financeKeys.sessionCourante(labId)]
        : [financeKeys.sessionCourante(labId)],
    },
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// finance (06) — cash-desk extensions data layer.
//
// Endpoints consumed (all under /api/v1):
//   POST /finance/caisse/:id/cloturer-z      finance.caisse.close   (Z-freeze)
//   POST /finance/caisse/:id/rouvrir         finance.caisse.reopen
//   GET  /finance/caisse/:id/x-report        finance.caisse.read
//   POST /finance/caisse/:id/mouvements      finance.mouvement.create
//   POST /finance/mouvements/:id/annuler     finance.mouvement.create
//   POST /finance/corrections                finance.paiement.refund
//   GET  /finance/caisse/:id/corrections     finance.cloture.read
//   GET  /finance/fidelite/bareme            finance.fidelite.read
//   PUT  /finance/fidelite/bareme            finance.fidelite.manage
//   POST /finance/fidelite/cartes/:id/accrual  finance.fidelite.manage
//   POST /finance/fidelite/cartes/:id/annuler  finance.fidelite.manage
// ═════════════════════════════════════════════════════════════════════════════

const idStr = (id: WireId): string => String(id);

// ── X-report (mid-session live snapshot) ────────────────────────────────────────

/** GET /finance/caisse/:id/x-report — same Z shape, no freeze. */
export function useXReport(labId: string | null, sessionId: string | null, enabled: boolean) {
  return useApiQuery<XReport>(
    financeKeys.xReport(labId, sessionId ?? "_"),
    `/finance/caisse/${sessionId ?? "_"}/x-report`,
    { enabled: enabled && !!sessionId, staleTime: 5_000 },
  );
}

// ── Z-freeze close + audited reopen ─────────────────────────────────────────────

export interface CloseZVars {
  sessionId: string;
  especes_comptees: number;
}

/** POST /finance/caisse/:id/cloturer-z — persist + FREEZE the Z. */
export function useCloseZ(labId: string | null) {
  return useApiMutation<CloseZResult, CloseZVars>(
    ({ sessionId, especes_comptees }) =>
      api.post<CloseZResult>(`/finance/caisse/${sessionId}/cloturer-z`, { especes_comptees }),
    { invalidate: [financeKeys.sessionCourante(labId)] },
  );
}

export interface ReopenVars {
  sessionId: string;
  motif: string;
}

/** POST /finance/caisse/:id/rouvrir — audited reopen (within the config window). */
export function useReopen(labId: string | null) {
  return useApiMutation<ReopenResult, ReopenVars>(
    ({ sessionId, motif }) => api.post<ReopenResult>(`/finance/caisse/${sessionId}/rouvrir`, { motif }),
    { invalidate: [financeKeys.sessionCourante(labId)] },
  );
}

// ── CaisseMouvement ledger + cash-out ───────────────────────────────────────────

export interface AddMouvementVars {
  sens: Sens;
  montant: number;
  motif: string;
  reference?: string;
}

/** POST /finance/caisse/:id/mouvements — add a cash in/out (sortie is drawer-guarded). */
export function useAddMouvement(labId: string | null, sessionId: string) {
  return useApiMutation<AddMouvementResult, AddMouvementVars>(
    ({ sens, montant, motif, reference }) => {
      const body: Record<string, unknown> = { sens, montant, motif };
      if (reference && reference.trim()) body.reference = reference.trim();
      return api.post<AddMouvementResult>(`/finance/caisse/${sessionId}/mouvements`, body);
    },
    {
      invalidate: [
        financeKeys.xReport(labId, sessionId),
        financeKeys.sessionStats(labId, sessionId),
        financeKeys.sessionCourante(labId),
      ],
    },
  );
}

export interface CancelMouvementVars {
  mouvementId: string;
  motif: string;
}

/** POST /finance/mouvements/:id/annuler — offsetting cancel (L-1, never a delete). */
export function useCancelMouvement(labId: string | null, sessionId: string) {
  return useApiMutation<CancelMouvementResult, CancelMouvementVars>(
    ({ mouvementId, motif }) =>
      api.post<CancelMouvementResult>(`/finance/mouvements/${mouvementId}/annuler`, { motif }),
    {
      invalidate: [
        financeKeys.xReport(labId, sessionId),
        financeKeys.sessionStats(labId, sessionId),
        financeKeys.sessionCourante(labId),
      ],
    },
  );
}

// ── Closed-session correction ────────────────────────────────────────────────────

/** GET /finance/caisse/:id/corrections — corrections posted against a drawer. */
export function useCorrections(labId: string | null, sessionId: string | null, enabled: boolean) {
  return useApiQuery<CorrectionCaisse[]>(
    financeKeys.corrections(labId, sessionId ?? "_"),
    `/finance/caisse/${sessionId ?? "_"}/corrections`,
    { enabled: enabled && !!sessionId, staleTime: 5_000 },
  );
}

export interface PostCorrectionVars {
  session_origine_id?: string;
  type?: CorrectionType;
  sens: Sens;
  montant: number;
  motif: string;
  facture_id?: string;
}

/**
 * POST /finance/corrections — refund/adjust a CLOSED session against the OPEN drawer.
 * `openSessionId` is the live drawer the correction posts to (parameterized at hook level so the
 * invalidations are static); the closed origin is carried per-call as `session_origine_id`.
 */
export function usePostCorrection(labId: string | null, openSessionId: string) {
  return useApiMutation<PostCorrectionResult, PostCorrectionVars>(
    (vars) => {
      const body: Record<string, unknown> = {
        open_session_id: openSessionId,
        sens: vars.sens,
        montant: vars.montant,
        motif: vars.motif,
      };
      if (vars.session_origine_id) body.session_origine_id = vars.session_origine_id;
      if (vars.type) body.type = vars.type;
      if (vars.facture_id) body.facture_id = vars.facture_id;
      return api.post<PostCorrectionResult>("/finance/corrections", body);
    },
    {
      invalidate: [
        financeKeys.corrections(labId, openSessionId),
        financeKeys.xReport(labId, openSessionId),
        financeKeys.sessionCourante(labId),
      ],
    },
  );
}

// ── Fidélité — bareme + card accrual/reversal ────────────────────────────────────

/** GET /finance/fidelite/bareme — the per-lab loyalty scale (falls back to config default). */
export function useBareme(labId: string | null, enabled: boolean) {
  return useApiQuery<BaremeFidelite>(financeKeys.bareme(labId), "/finance/fidelite/bareme", {
    enabled,
    staleTime: 30_000,
  });
}

export interface PutBaremeVars {
  points_par_dinar: number;
  paliers: BaremeFidelite["paliers"];
}

/** PUT /finance/fidelite/bareme — upsert the scale. */
export function usePutBareme(labId: string | null) {
  return useApiMutation<BaremeFidelite, PutBaremeVars>(
    (vars) => api.put<BaremeFidelite>("/finance/fidelite/bareme", vars),
    { invalidate: [financeKeys.bareme(labId)] },
  );
}

export interface AccrualVars {
  carteId: string;
  facture_id?: string;
  net_paid_centimes: number;
}

/** POST /finance/fidelite/cartes/:id/accrual — settle a sale's accrual on the card. */
export function useAccrual() {
  return useApiMutation<AccrualResult, AccrualVars>(({ carteId, facture_id, net_paid_centimes }) => {
    const body: Record<string, unknown> = { net_paid_centimes };
    if (facture_id && facture_id.trim()) body.facture_id = facture_id.trim();
    return api.post<AccrualResult>(`/finance/fidelite/cartes/${idStr(carteId)}/accrual`, body);
  });
}

export interface ReverseVars {
  carteId: string;
  points: number;
  note?: string;
}

/** POST /finance/fidelite/cartes/:id/annuler — reverse points (offsetting annulation). */
export function useReverse() {
  return useApiMutation<ReverseResult, ReverseVars>(({ carteId, points, note }) => {
    const body: Record<string, unknown> = { points };
    if (note && note.trim()) body.note = note.trim();
    return api.post<ReverseResult>(`/finance/fidelite/cartes/${idStr(carteId)}/annuler`, body);
  });
}
